
import { MessageSquare, User } from "lucide-react";
import { format } from "date-fns";
import { Avatar } from "@/components/ui/avatar";

interface CallTranscriptSectionProps {
  transcript: string | null;
}

export const CallTranscriptSection = ({ transcript }: CallTranscriptSectionProps) => {
  if (!transcript) {
    return (
      <div>
        <h3 className="text-sm font-medium mb-2 flex items-center">
          <MessageSquare className="h-4 w-4 mr-2" />
          Transcript
        </h3>
        <div className="bg-muted/20 rounded-md p-4 text-sm text-muted-foreground text-center py-8">
          <p>No transcript available for this call yet</p>
        </div>
      </div>
    );
  }

  // Enhanced parsing of transcript for better display
  const messages = [];
  const lines = transcript.split('\n');
  
  let currentRole = null;
  let currentContent = '';
  let currentTimestamp = new Date(); // Default timestamp
  
  for (const line of lines) {
    const trimmedLine = line.trim();
    if (!trimmedLine) continue;
    
    // Check for role indicators
    if (
      trimmedLine.toLowerCase().startsWith('agent:') || 
      trimmedLine.toLowerCase().startsWith('ai:') ||
      trimmedLine.toLowerCase().startsWith('assistant:')
    ) {
      // If we had content for a previous role, push it
      if (currentRole && currentContent) {
        messages.push({ 
          role: currentRole, 
          content: currentContent.trim(),
          timestamp: new Date(currentTimestamp) // Clone the timestamp
        });
      }
      
      currentRole = 'agent';
      currentContent = trimmedLine.replace(/^(Agent|AI|Assistant):?\s*/i, '').trim();
      // Set a timestamp a few seconds ahead for each message to simulate a timeline
      currentTimestamp = new Date(currentTimestamp.getTime() + 30000); // Add 30 seconds
    } 
    else if (
      trimmedLine.toLowerCase().startsWith('user:') || 
      trimmedLine.toLowerCase().startsWith('human:') ||
      trimmedLine.toLowerCase().startsWith('customer:') ||
      trimmedLine.toLowerCase().startsWith('caller:')
    ) {
      // If we had content for a previous role, push it
      if (currentRole && currentContent) {
        messages.push({ 
          role: currentRole, 
          content: currentContent.trim(),
          timestamp: new Date(currentTimestamp)
        });
      }
      
      currentRole = 'user';
      currentContent = trimmedLine.replace(/^(User|Human|Customer|Caller):?\s*/i, '').trim();
      // Set a timestamp a few seconds ahead
      currentTimestamp = new Date(currentTimestamp.getTime() + 30000); // Add 30 seconds
    }
    else if (currentRole) {
      // Continue with current role
      currentContent += ' ' + trimmedLine;
    }
    else {
      // If no role detected yet, assume it's the agent starting
      currentRole = 'agent';
      currentContent = trimmedLine;
    }
  }
  
  // Add the last message if there is one
  if (currentRole && currentContent) {
    messages.push({ 
      role: currentRole, 
      content: currentContent.trim(),
      timestamp: new Date(currentTimestamp)
    });
  }
  
  // If we couldn't parse properly, fallback to showing the raw transcript
  if (messages.length === 0) {
    return (
      <div>
        <h3 className="text-sm font-medium mb-2 flex items-center">
          <MessageSquare className="h-4 w-4 mr-2" />
          Transcript
        </h3>
        <div className="bg-muted/20 rounded-md p-4">
          <p className="whitespace-pre-line text-sm">{transcript}</p>
        </div>
      </div>
    );
  }

  // Format the timestamp to display
  const formatMessageTime = (date: Date) => {
    return format(date, "h:mma").toLowerCase();
  };

  // Group messages by sender (consecutive messages from the same role are grouped together)
  const renderMessages = () => {
    const messageGroups = [];
    let currentSender = null;
    let currentGroup = [];

    for (const message of messages) {
      if (message.role !== currentSender) {
        if (currentGroup.length > 0) {
          messageGroups.push({
            role: currentSender,
            messages: [...currentGroup],
          });
        }
        currentSender = message.role;
        currentGroup = [message];
      } else {
        currentGroup.push(message);
      }
    }

    // Add the last group
    if (currentGroup.length > 0) {
      messageGroups.push({
        role: currentSender,
        messages: [...currentGroup],
      });
    }

    return messageGroups.map((group, groupIndex) => (
      <div key={groupIndex} className="mb-6">
        {/* Sender Name */}
        <div className="text-sm font-medium text-gray-600 mb-1">
          {group.role === 'agent' ? 'Agent' : 'Customer'}
        </div>
        
        {/* Messages */}
        <div className="space-y-2">
          {group.messages.map((message, messageIndex) => (
            <div 
              key={`${groupIndex}-${messageIndex}`} 
              className={`relative flex items-start ${message.role === 'agent' ? 'justify-start' : 'justify-end'}`}
            >
              <div 
                className={`
                  rounded-2xl px-4 py-3 max-w-[85%] shadow-sm
                  ${message.role === 'agent' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-100 text-gray-800'
                  }
                `}
              >
                <p className="text-sm">{message.content}</p>
              </div>
              <div className={`text-xs text-gray-500 mt-1 ${message.role === 'agent' ? 'ml-2' : 'mr-2'}`}>
                {formatMessageTime(message.timestamp)}
              </div>
            </div>
          ))}
        </div>
      </div>
    ));
  };

  return (
    <div>
      <h3 className="text-sm font-medium mb-3 flex items-center">
        <MessageSquare className="h-4 w-4 mr-2" />
        Transcript
      </h3>
      
      <div className="rounded-md border p-4 bg-card">
        {renderMessages()}
      </div>
    </div>
  );
};
